import {
  PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN_STORE_US,
  PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN_STORE_UK,
  PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN_STORE_EU,
  PUBLIC_SHOPIFY_STORE_NAME_US,
  PUBLIC_SHOPIFY_STORE_NAME_UK,
  PUBLIC_SHOPIFY_STORE_NAME_EU
} from '$env/static/public';

import { derived } from 'svelte/store';
import { getSdk, type Requester } from '$gql/storefront/sdk';
import { region } from './region';
import { getStorefrontGraphqlEndpoint } from './constants';

export const createFetcher =
  ({
    url,
    token,
    authHeader = 'X-Shopify-Storefront-Access-Token'
  }: {
    url: string;
    token: string;
    authHeader?: string;
  }): Requester<RequestInit, unknown> =>
  async (doc, vars, options) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [authHeader]: token
      },
      cache: 'no-store',
      body: JSON.stringify({ query: doc.loc?.source.body, variables: vars }),
      ...options
    });

    if (!res.ok) throw new Error(`Request failed: ${res.status} ${res.statusText}`);

    const json = await res.json();

    if ('errors' in json && Array.isArray(json.errors) && json.errors.length > 0) {
      throw json.errors;
    }

    return json.data;
  };

export const fetchers = {
  us: getSdk(
    createFetcher({
      url: getStorefrontGraphqlEndpoint(PUBLIC_SHOPIFY_STORE_NAME_US),
      token: PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN_STORE_US
    })
  ),
  uk: getSdk(
    createFetcher({
      url: getStorefrontGraphqlEndpoint(PUBLIC_SHOPIFY_STORE_NAME_UK),
      token: PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN_STORE_UK
    })
  ),
  eu: getSdk(
    createFetcher({
      url: getStorefrontGraphqlEndpoint(PUBLIC_SHOPIFY_STORE_NAME_EU),
      token: PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN_STORE_EU
    })
  )
};

export const shopify = derived(region, ($region) => {
  return fetchers[$region];
});

/**
 * images from shopify should contain something like:
 * Just width: https://cdn.shopify.com/.../[Filename]_[width]x.jpg.webp?v=1680532315
 * Width and height: https://cdn.shopify.com/.../[Filename]_[width]x[height].jpg.webp?v=1680532315
 *
 * we should be able to replace the width and height with our own values
 */
export const resizeShopifyImage = (url: string, width: number, height?: number): string => {
  const regex = /_(\d+)x(\d+)*./;

  if (!width || !regex.test(url)) return url;

  return url.replace(regex, `_${width}x${height ?? ''}.`);
};
